import { Box, Paper } from "@mui/material";
import i18n from "i18next";
import { useMemo, useState } from "react";
import { CustomerOrderProfitabilityAnalysisApi } from "src/api/generated/erp/sales/customerOrder/profitability/api/customerOrderProfitabilityAnalysisApi.ts";
import { CustomerOrderProfitabilityAnalysisDto } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityAnalysisDto.ts";
import { CustomerOrderProfitabilityLineKey } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityLineKey.ts";
import { CustomerOrderProfitabilityPlanLineInput } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityPlanLineInput.ts";
import { CustomerOrderProfitabilityRowDto } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityRowDto.ts";
import { CustomerOrderProfitabilityPlanLineScenarioType } from "src/api/generated/erp/db/types/enums/customerOrderProfitabilityPlanLineScenarioType.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncFetchRefreshFunc, AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CustomerOrderProfitabilityActions } from "src/components/views/erp/sales/customerOrder/profitability/CustomerOrderProfitabilityActions.tsx";
import { CustomerOrderProfitabilityCharts } from "src/components/views/erp/sales/customerOrder/profitability/CustomerOrderProfitabilityCharts.tsx";
import { CustomerOrderProfitabilityHistoryView } from "src/components/views/erp/sales/customerOrder/profitability/CustomerOrderProfitabilityHistoryView.tsx";
import { CustomerOrderProfitabilityTable } from "src/components/views/erp/sales/customerOrder/profitability/CustomerOrderProfitabilityTable.tsx";
import {
	isCustomerOrderProfitabilityLineKeyEditable,
	sortCustomerOrderProfitabilityRows,
	toCustomerOrderProfitabilityPlanLineKey,
} from "src/components/views/erp/sales/customerOrder/profitability/customerOrderProfitabilityLineKeyUtils.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderProfitabilityViewProps {
	customerOrderId: number;
	onDataDirtyStateChanged: DataDirtyStateChangeHandler;
}

type DraftValuesByLineKey = Partial<Record<CustomerOrderProfitabilityLineKey, { budget: number; forecast: number }>>;

export const CustomerOrderProfitabilityView = ({ customerOrderId, ...other }: CustomerOrderProfitabilityViewProps) => {
	return (
		<AsyncFetchRender
			fetch={() => CustomerOrderProfitabilityAnalysisApi.getAnalysis({ customerOrderId })}
			content={(data, reload) => (
				<AsyncContent customerOrderId={customerOrderId} initialAnalysis={data} reload={reload} {...other} />
			)}
		/>
	);
};

interface AsyncContentProps extends CustomerOrderProfitabilityViewProps {
	initialAnalysis: CustomerOrderProfitabilityAnalysisDto;
	reload: AsyncFetchRefreshFunc<CustomerOrderProfitabilityAnalysisDto>;
}

const AsyncContent = ({ customerOrderId, onDataDirtyStateChanged, initialAnalysis, reload }: AsyncContentProps) => {
	const { openDialog } = useGenericDialog();
	const [analysis, setAnalysis] = useState(initialAnalysis);
	// Keeps the user's unsaved budget/forecast edits separate from the latest persisted analysis.
	const [draftValuesByLineKey, setDraftValuesByLineKey] = useState(createDraftValues(initialAnalysis.rows));
	const [isEditMode, setIsEditMode] = useState(false);

	// Only editable rows can produce plan line updates.
	const editableRows = useMemo(
		() => analysis.rows.filter((row) => isCustomerOrderProfitabilityLineKeyEditable(row.lineKey)),
		[analysis.rows],
	);
	// Merge persisted rows with local draft values so edits are visible immediately in the table and charts.
	const displayedRows = useMemo(() => {
		return sortCustomerOrderProfitabilityRows(
			analysis.rows.map((row) => ({
				...row,
				budget: draftValuesByLineKey[row.lineKey]?.budget ?? row.budget,
				forecast: draftValuesByLineKey[row.lineKey]?.forecast ?? row.forecast,
			})),
		);
	}, [analysis.rows, draftValuesByLineKey]);

	const displayedRowsByKey = useMemo(
		() =>
			Object.fromEntries(displayedRows.map((row) => [row.lineKey, row])) as Partial<
				Record<CustomerOrderProfitabilityLineKey, CustomerOrderProfitabilityRowDto>
			>,
		[displayedRows],
	);

	return (
		<VerticalBox
			sx={{
				flex: 1,
				minHeight: 0,
				overflow: "auto",
			}}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "minmax(0, 1fr)",
					gridTemplateRows: "auto auto",
					gap: 2,
					flex: "0 0 auto",
					minHeight: 0,
					padding: 2,
					overflow: "visible",
					alignContent: "start",
				}}
			>
				<Paper
					sx={{
						display: "flex",
						flexDirection: "column",
						minWidth: 0,
						minHeight: 420,
						overflow: "hidden",
					}}
				>
					<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
						<CustomerOrderProfitabilityActions
							isEditMode={isEditMode}
							onRefresh={handleRefresh}
							onOpenHistory={handleOpenHistory}
							onEdit={handleEdit}
							onUndo={handleUndo}
							onSave={handleSave}
						/>
						<CustomerOrderProfitabilityTable
							rows={displayedRows}
							domesticCurrencyCode={analysis.domesticCurrencyCode}
							isEditMode={isEditMode}
							onBudgetChange={handleBudgetChange}
							onForecastChange={handleForecastChange}
						/>
					</VerticalBox>
				</Paper>

				<CustomerOrderProfitabilityCharts
					rowsByKey={displayedRowsByKey}
					domesticCurrencyCode={analysis.domesticCurrencyCode}
				/>
			</Box>
		</VerticalBox>
	);

	function handleBudgetChange(row: CustomerOrderProfitabilityRowDto, value: number) {
		setDraftValuesByLineKey((prev) => ({
			...prev,
			[row.lineKey]: {
				budget: value,
				forecast: prev[row.lineKey]?.forecast ?? row.forecast,
			},
		}));
		onDataDirtyStateChanged({ isDirty: true });
	}

	function handleForecastChange(row: CustomerOrderProfitabilityRowDto, value: number) {
		setDraftValuesByLineKey((prev) => ({
			...prev,
			[row.lineKey]: {
				budget: prev[row.lineKey]?.budget ?? row.budget,
				forecast: value,
			},
		}));
		onDataDirtyStateChanged({ isDirty: true });
	}

	async function handleRefresh() {
		const nextAnalysis = await reload();
		applyAnalysis(nextAnalysis);
	}

	async function handleOpenHistory() {
		openDialog({
			title: i18n.t("history"),
			size: "lg",
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrderProfitabilityHistory.dialog"),
				pathSegments: [i18n.t("history")],
				data: {
					customerOrderId,
				},
			},
			content: <CustomerOrderProfitabilityHistoryView customerOrderId={customerOrderId} />,
		});
	}

	async function handleEdit() {
		resetDraftValues();
		setIsEditMode(true);
	}

	async function handleUndo() {
		resetDraftValues();
		setIsEditMode(false);
		onDataDirtyStateChanged({ isDirty: false });
	}

	async function handleSave() {
		const nextAnalysis = await CustomerOrderProfitabilityAnalysisApi.savePlanLines({
			customerOrderId,
			updates: createUpdates(editableRows, draftValuesByLineKey),
		});
		applyAnalysis(nextAnalysis);
		onDataDirtyStateChanged({ isDirty: false });
	}

	function applyAnalysis(nextAnalysis: CustomerOrderProfitabilityAnalysisDto) {
		setAnalysis(nextAnalysis);
		setDraftValuesByLineKey(createDraftValues(nextAnalysis.rows));
		setIsEditMode(false);
	}

	function resetDraftValues() {
		setDraftValuesByLineKey(createDraftValues(analysis.rows));
	}
};

const createDraftValues = (rows: Array<CustomerOrderProfitabilityRowDto>): DraftValuesByLineKey => {
	// Initialize editable rows with their persisted budget/forecast values so edit mode starts from the backend state.
	return Object.fromEntries(
		rows
			.filter((row) => isCustomerOrderProfitabilityLineKeyEditable(row.lineKey))
			.map((row) => [
				row.lineKey,
				{
					budget: row.budget,
					forecast: row.forecast,
				},
			]),
	);
};

const createUpdates = (
	rows: Array<CustomerOrderProfitabilityRowDto>,
	draftValuesByLineKey: DraftValuesByLineKey,
): Array<CustomerOrderProfitabilityPlanLineInput> => {
	// Convert row drafts into the backend save format:
	// one plan line per scenario type, excluding values that still match the persisted row.
	return rows.flatMap((row) => {
		const draftValues = draftValuesByLineKey[row.lineKey] ?? {
			budget: row.budget,
			forecast: row.forecast,
		};

		return [
			{
				scenarioType: "BUDGET" as CustomerOrderProfitabilityPlanLineScenarioType,
				lineKey: toCustomerOrderProfitabilityPlanLineKey(row.lineKey),
				amount: draftValues.budget,
			},
			{
				scenarioType: "FORECAST" as CustomerOrderProfitabilityPlanLineScenarioType,
				lineKey: toCustomerOrderProfitabilityPlanLineKey(row.lineKey),
				amount: draftValues.forecast,
			},
		].filter((update) => {
			switch (update.scenarioType) {
				case "BUDGET":
					return update.amount !== row.budget;
				case "FORECAST":
					return update.amount !== row.forecast;
			}
		});
	});
};
